import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Tooltip,
  ListSubheader,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useEmailTemplates } from '../../hooks/useEmails';

const CATEGORY_LABELS = {
  document_request: 'Document Request',
  status_update: 'Status Update',
  reminder: 'Reminder',
  follow_up: 'Follow Up',
  custom: 'Custom',
};

const CATEGORY_ORDER = ['document_request', 'status_update', 'reminder', 'follow_up', 'custom'];

function snippet(body, maxLen = 60) {
  if (!body) return '';
  const text = String(body).replace(/<[^>]*>/g, '').trim();
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

export default function TemplatePickerDialog({ open, onClose, onSelect }) {
  const { data: templates = [], isLoading: loading } = useEmailTemplates({ enabled: open });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (!q) return true;
      const name = (t.name || '').toLowerCase();
      const subj = (t.subject_template || '').toLowerCase();
      const body = snippet(t.body_template || '', 500).toLowerCase();
      return name.includes(q) || subj.includes(q) || body.includes(q);
    });
  }, [templates, search]);

  const grouped = useMemo(() => {
    const map = {};
    CATEGORY_ORDER.forEach((k) => {
      map[k] = [];
    });
    filtered.forEach((t) => {
      const cat = t.category && map[t.category] !== undefined ? t.category : 'custom';
      if (!map[cat]) map[cat] = [];
      map[cat].push(t);
    });
    return map;
  }, [filtered]);

  const handleInsert = () => {
    if (selected) {
      onSelect(selected);
      onClose();
      setSelected(null);
      setSearch('');
    }
  };

  const handleClose = () => {
    onClose();
    setSelected(null);
    setSearch('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Choose a template</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search templates…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : templates.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No templates yet — create one in Settings → Email Templates
          </Typography>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No templates match your search.
          </Typography>
        ) : (
          <List dense disablePadding>
            {CATEGORY_ORDER.map((cat) => {
              const items = grouped[cat] || [];
              if (!items.length) return null;
              return (
                <React.Fragment key={cat}>
                  <ListSubheader disableSticky sx={{ bgcolor: 'background.default', lineHeight: 2 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      {CATEGORY_LABELS[cat] || cat}
                    </Typography>
                  </ListSubheader>
                  {items.map((tpl) => {
                    const preview = snippet(tpl.body_template || tpl.body || '');
                    const fullBody = tpl.body_template || tpl.body || '';
                    return (
                      <ListItem key={tpl.id} disablePadding sx={{ mb: 0.5 }}>
                        <Tooltip
                          title={
                            <Typography variant="caption" component="div" sx={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>
                              {fullBody.replace(/<[^>]*>/g, '')}
                            </Typography>
                          }
                          placement="right"
                          enterDelay={400}
                        >
                          <ListItemButton
                            selected={selected?.id === tpl.id}
                            onClick={() => setSelected(tpl)}
                            sx={{
                              borderRadius: 2,
                              border: 1,
                              borderColor: selected?.id === tpl.id ? 'primary.main' : 'divider',
                              alignItems: 'flex-start',
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" fontWeight={600}>
                                    {tpl.name}
                                  </Typography>
                                  <Chip
                                    label={CATEGORY_LABELS[tpl.category] || tpl.category || 'Custom'}
                                    size="small"
                                    sx={{ fontSize: 11 }}
                                  />
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {preview || '(empty body)'}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleInsert} disabled={!selected}>
          Use template
        </Button>
      </DialogActions>
    </Dialog>
  );
}
